import objectPath from 'object-path'
import { debounceCallback } from 'utility/debounceCallback.util'
import { getCurrentUi } from 'designer/component/action/Data'
import { onSyncData } from './action/SyncData'
import { onRuleDisable, onRuleEnable } from './action/EnableDisable'
import { onRuleHide, onRuleShow } from './action/ShowHide'
import { onRuleShowHideList } from './action/ShowHideList'
import { onRuleRequire } from './action/Require'
import { onRuleWarning } from './action/Warning'
import { onRuleSet } from './action/Set'
import { onRuleReset } from './action/Reset'
import { onRuleColorLabel } from './action/ColorLabel'
import { onRuleUpdateValue } from './action/UpdateValue'
import {
  isRuleCallApiWaitMode,
  onRuleCallApi,
} from './action/CallApi'
import { onRuleCallTableApi } from './action/CallTableApi'
import { onRuleCalculate } from './action/Calculate'
import { onRuleCalculateDate } from './action/CalculateDate'
import { onRuleAgeToDate } from './action/AgeToDate'
import { onRuleDateToText } from './action/DateToText'
import {
  isRuleFetchOptionAction,
  onRuleFetchOption,
} from './action/FetchOption'
import { onRuleFetchTable } from './action/FetchTable'
import { onRuleFetchComponent } from './action/FetchComponent'
import { onRuleUpdateCheckboxList } from './action/UpdateCheckboxList'
import {
  onRuleSelectRowTable,
  onRuleUnselectRowTable,
} from './action/SelectRowTable'
import {
  onRuleActiveButton,
  onRuleClearActiveButton,
} from './action/ActiveButton'
import { onRuleClickButton } from './action/ClickButton'
import {
  onRuleActiveTab,
  onRuleClearActiveTab,
} from './action/ActiveTab'
import { onRuleClearActive } from './action/ClearActive'
import {
  onRuleDoneLoading,
  onRuleLoading,
} from './action/Loading'
import { onRuleCloseModal } from './action/CloseModal'
import { onRuleOpenModal } from './action/OpenModal'
import { onRuleSweetAlert } from './action/SweetAlert'
import { onRuleCopy } from './action/Copy'
import { onRuleGoToUrl } from './action/GoToUrl'
import { onRuleReload } from './action/Reload'
import { onRuleShowLoadUI } from './action/ShowLoadUI'
import { compareRuleValue } from './Compare'

export {
  calculateRuleFormula,
  getRuleFormula,
  getRuleFormulas,
} from './action/Calculate'
export { compareRuleValue, isValidThaiCitizenId } from './Compare'

export const getRules = (jsonForm = {}) => {
  return objectPath.get(jsonForm, 'rules') || []
}

export const hasRules = (jsonForm = {}) => {
  return getRules(jsonForm).length > 0
}

const getTriggerEvents = (trigger = {}) => {
  if (Array.isArray(trigger.events)) {
    return trigger.events
      .map((triggerEvent) => {
        if (typeof triggerEvent === 'string') {
          return {
            event: triggerEvent,
            debounce: Number(trigger.debounce || 0),
          }
        }

        return {
          event: triggerEvent.event || '',
          debounce: Number(triggerEvent.debounce ?? trigger.debounce ?? 0),
        }
      })
      .filter(({ event }) => event)
  }

  return trigger.event
    ? [
        {
          event: trigger.event,
          debounce: Number(trigger.debounce || 0),
        },
      ]
    : []
}

const getTriggerFields = (trigger = {}) => {
  if (Array.isArray(trigger.fields)) return trigger.fields
  return []
}

const getTriggerKeys = (trigger = {}) => {
  if (Array.isArray(trigger.keys)) return trigger.keys
  return trigger.key ? [trigger.key] : []
}

const getFieldKey = (key = '') => {
  let keyText = String(key || '')
  let keyList = keyText.split(':')

  return keyList[keyList.length - 1]
}

const normalizeRuleFieldKey = (key = '') => {
  let fieldKey = getFieldKey(key)

  if (fieldKey[0] === '@' && fieldKey.includes('-')) {
    let keyList = fieldKey.split('-')
    let groupKey = keyList.shift()

    return [groupKey, keyList.join('-')].filter((value) => value).join('.')
  }

  return fieldKey
}

const removeRuleKeyIndex = (key = '') => {
  return String(key || '')
    .split('.')
    .filter((value) => value && !/^\d+$/.test(value))
    .join('.')
}

const normalizeRuleMatchKey = (key = '') => {
  return removeRuleKeyIndex(normalizeRuleFieldKey(key))
}

const getRuleKeyParts = (key = '') => {
  return normalizeRuleMatchKey(key)
    .split('.')
    .filter((value) => value)
}

const isRuleScopedKey = (key = '') => {
  return getRuleKeyParts(key).some((part) => part.startsWith('@'))
}

const isCrossScopeShortMatch = (ruleKey = '', uiKey = '') => {
  let ruleParts = getRuleKeyParts(ruleKey)
  let uiParts = getRuleKeyParts(uiKey)
  let hasScope = isRuleScopedKey(ruleKey) || isRuleScopedKey(uiKey)
  let hasShortKey = ruleParts.length === 1 || uiParts.length === 1

  return hasScope && hasShortKey
}

const matchRulePathKey = (ruleKey, uiKey) => {
  let ruleMatchKey = normalizeRuleMatchKey(ruleKey)
  let uiMatchKey = normalizeRuleMatchKey(uiKey)

  if (!ruleMatchKey || !uiMatchKey) return false
  if (ruleMatchKey === uiMatchKey) return true
  if (isCrossScopeShortMatch(ruleKey, uiKey)) return false
  if (uiMatchKey.indexOf(`${ruleMatchKey}.`) === 0) return true
  if (ruleMatchKey.indexOf(`${uiMatchKey}.`) === 0) return true
  if (uiMatchKey.endsWith(`.${ruleMatchKey}`)) return true
  if (ruleMatchKey.endsWith(`.${uiMatchKey}`)) return true

  return false
}

const matchRuleKey = (ruleKey, uiKey) => {
  if (!uiKey) return true
  if (!ruleKey) return false

  return (
    ruleKey === uiKey ||
    getFieldKey(ruleKey) === uiKey ||
    normalizeRuleFieldKey(ruleKey) === normalizeRuleFieldKey(uiKey) ||
    matchRulePathKey(ruleKey, uiKey)
  )
}

const matchRuleKeys = (ruleKey, uiKey) => {
  let uiKeys = Array.isArray(uiKey) ? uiKey : [uiKey]

  return uiKeys.filter((key) => key).some((key) => matchRuleKey(ruleKey, key))
}

const addRuleMatchKey = (keys = [], key) => {
  if (!key) return keys
  if (keys.includes(key)) return keys

  keys.push(key)
  return keys
}

const addRuleMapKeys = (keys = [], mapKey = '') => {
  let keyList = String(mapKey || '')
    .split('.')
    .filter((key) => key)

  keyList.forEach((key, index) => {
    addRuleMatchKey(keys, keyList.slice(index).join('.'))
  })

  return keys
}

const getRuleUiMapKeys = (jsonForm = {}, key) => {
  let mapKey = objectPath.get(jsonForm, 'schema.mapKey') || {}
  let keys = []
  let matchKeys = [key, ...getRuleKeyParts(key)]

  matchKeys.forEach((matchKey) => {
    addRuleMapKeys(keys, objectPath.get(mapKey, matchKey))
  })

  return keys
}

const getRuleMapFieldKeys = (jsonForm = {}, key) => {
  let mapKey = objectPath.get(jsonForm, 'schema.mapKey') || {}
  let keys = []

  Object.keys(mapKey).forEach((mapFieldKey) => {
    let mapValue = mapKey[mapFieldKey] || ''
    if (matchRulePathKey(mapValue, key)) {
      addRuleMatchKey(keys, mapFieldKey)
      addRuleMapKeys(keys, mapValue)
    }
  })

  return keys
}

const getRuleSearchKeys = (jsonForm = {}, uiKey) => {
  let uiKeys = Array.isArray(uiKey) ? uiKey : [uiKey]
  let keys = []

  uiKeys
    .filter((key) => key)
    .forEach((key) => {
      addRuleMatchKey(keys, key)
      addRuleMatchKey(keys, normalizeRuleMatchKey(key))

      addRuleMapKeys(keys, normalizeRuleMatchKey(key))

      getRuleUiMapKeys(jsonForm, key).forEach((mapKey) => {
        addRuleMatchKey(keys, mapKey)
      })

      getRuleMapFieldKeys(jsonForm, key).forEach((mapKey) => {
        addRuleMatchKey(keys, mapKey)
      })

      getRuleFieldKeys(jsonForm, key).forEach((fieldKey) => {
        addRuleMatchKey(keys, fieldKey)
        addRuleMatchKey(keys, normalizeRuleMatchKey(fieldKey))
      })
    })

  return keys
}

const matchRuleEvent = (triggerField = {}, trigger = {}, eventKey) => {
  if (!eventKey) return true

  let fieldEvents = getTriggerEvents({ events: triggerField.events })
  let triggerEvents = fieldEvents.length
    ? fieldEvents
    : getTriggerEvents(trigger)

  return triggerEvents.find(({ event }) => event === eventKey)
}

const getMatchedTriggerEvent = (rule = {}, uiKey, eventKey) => {
  let { trigger = {} } = rule
  let triggerField = getTriggerFields(trigger).find(({ key }) =>
    matchRuleKeys(key, uiKey),
  )
  let actionTriggerField = getRuleActionTriggerFields(rule).find(({ key }) =>
    matchRuleKeys(key, uiKey),
  )
  let conditionTriggerField = getRuleConditionTriggerFields(rule).find(
    ({ key }) => matchRuleKeys(key, uiKey),
  )
  let triggerEvents = triggerField
    ? getTriggerEvents({ events: triggerField.events })
    : []

  if (!triggerEvents.length && actionTriggerField) {
    triggerEvents = getTriggerEvents({ events: actionTriggerField.events })
  }

  if (!triggerEvents.length && conditionTriggerField) {
    triggerEvents = getTriggerEvents({
      events: conditionTriggerField.events,
    })
  }

  if (!triggerEvents.length) {
    triggerEvents = getTriggerEvents(trigger)
  }

  if (eventKey) {
    return triggerEvents.find(({ event }) => event === eventKey)
  }

  return triggerEvents[0]
}

export const searchRules = (
  jsonForm = {},
  uiKey = null,
  { eventKey, returnRuleId } = {},
) => {
  let rules = getRules(jsonForm).filter(({ enabled }) => enabled)
  let ruleUiKey = getRuleSearchKeys(jsonForm, uiKey)
  if (!uiKey && !eventKey) {
    return returnRuleId ? rules.map(({ id }) => id) : rules
  }

  let filterRules = rules.filter((rule = {}) => {
    let { trigger = {} } = rule
    let triggerFields = getTriggerFields(trigger)
    let triggerKeys = getTriggerKeys(trigger)
    let matchTriggerFields = triggerFields.filter(({ key }) =>
      matchRuleKeys(key, ruleUiKey),
    )
    let actionTriggerFields = getRuleActionTriggerFields(rule)
    let matchActionTriggerFields = actionTriggerFields.filter(({ key }) =>
      matchRuleKeys(key, ruleUiKey),
    )
    let conditionTriggerFields = getRuleConditionTriggerFields(rule)
    let matchConditionTriggerFields = conditionTriggerFields.filter(({ key }) =>
      matchRuleKeys(key, ruleUiKey),
    )
    let hasUiKey = uiKey
      ? matchTriggerFields.length ||
        matchActionTriggerFields.length ||
        matchConditionTriggerFields.length ||
        triggerKeys.find((key) => matchRuleKeys(key, ruleUiKey)) ||
        matchRuleKeys(trigger.key, ruleUiKey)
      : true
    let hasEventKey = eventKey
      ? (uiKey && matchTriggerFields.length
          ? matchTriggerFields.find((triggerField) =>
              matchRuleEvent(triggerField, trigger, eventKey),
            )
          : uiKey && matchActionTriggerFields.length
          ? matchActionTriggerFields.find((triggerField) =>
              matchRuleEvent(triggerField, trigger, eventKey),
            )
          : uiKey && matchConditionTriggerFields.length
          ? matchConditionTriggerFields.find((triggerField) =>
              matchRuleEvent(triggerField, trigger, eventKey),
            )
          : triggerFields.find((triggerField) =>
              matchRuleEvent(triggerField, trigger, eventKey),
            )) || matchRuleEvent({}, trigger, eventKey)
      : true

    return hasUiKey && hasEventKey
  })

  return returnRuleId ? filterRules.map(({ id }) => id) : filterRules
}

const getSourceData = (source, designContext = {}, data = {}) => {
  let { jsonForm = {} } = designContext
  let formData = data.formData || jsonForm.formData || {}

  switch (source) {
    case 'response':
      return data.response || objectPath.get(formData, '@response') || {}
    case 'datasource':
    case 'datasources':
      return (
        data.datasource ||
        data.datasources ||
        designContext.datasourceValue ||
        objectPath.get(designContext, '@datasource') ||
        jsonForm.datasources ||
        {}
      )
    case 'urlParam':
    case 'pathParams':
    case 'queryParams':
      return (
        data.urlParams ||
        designContext.urlParams ||
        objectPath.get(designContext, '@datasource.pathParam') ||
        objectPath.get(designContext, '@datasource.queryParam') ||
        {}
      )
    case 'condition':
      return objectPath.get(formData, '@condition') || {}
    case 'provider':
      return data.provider || designContext.provider || {}
    case 'user':
      return data.user || designContext.user || {}
    case 'perspective':
      return data.urlParams || designContext.urlParams || {}
    case 'setting':
      return (
        jsonForm.setting ||
        jsonForm.settings ||
        objectPath.get(jsonForm, 'uischema.setting') ||
        objectPath.get(jsonForm, 'uischema.settings') ||
        {}
      )
    case 'config':
      return jsonForm.accountConfig || designContext.config || []
    case 'localStorage':
      return null
    case 'formData':
    default:
      return formData
  }
}

const findRuleUi = (elements = [], fieldKey) => {
  if (!fieldKey) return null

  let fieldName = getFieldKey(fieldKey)

  for (let element of elements) {
    let input = element.input || {}
    let isElementMatch =
      element.key === fieldName ||
      element.name === fieldName ||
      element.value === fieldName ||
      getFieldKey(element.name) === fieldName ||
      getFieldKey(element.value) === fieldName
    let isInputMatch =
      input.key === fieldName ||
      input.name === fieldName ||
      input.value === fieldName ||
      getFieldKey(input.name) === fieldName ||
      getFieldKey(input.value) === fieldName

    if (isElementMatch) return element
    if (isInputMatch) return input

    let childElement = findRuleUi(element.elements, fieldName)
    if (childElement) return childElement
  }

  return null
}

const uniqueRuleKeys = (keys = []) => {
  return keys.filter((key, index) => key && keys.indexOf(key) === index)
}

const ruleContainerKeys = [
  'header',
  'body',
  'footer',
  'condition',
  'pathParam',
  'queryParam',
  'datasources',
  'provider',
  'response',
  'user',
  'perspective',
  'setting',
  'settings',
  'config',
  'accountConfig',
  'localStorage',
]

const isRuleFieldCandidateKey = (key = '') => {
  let keyText = String(key || '').trim()

  if (!keyText) return false
  if (ruleContainerKeys.includes(keyText)) return false
  if (keyText.includes(' > ')) return false

  return true
}

const addRuleFieldCandidateKey = (keys = [], key = '') => {
  if (!isRuleFieldCandidateKey(key)) return keys

  return addRuleMatchKey(keys, String(key || '').trim())
}

const addRuleFieldOptionKeyParts = (keys = [], key = '') => {
  let keyText = String(key || '').trim()

  if (!keyText) return keys
  if (!keyText.includes('|')) {
    return addRuleFieldCandidateKey(keys, keyText)
  }

  let optionParts = keyText
    .split('|')
    .map((value) => value.trim())
    .filter((value) => isRuleFieldCandidateKey(value))

  optionParts
    .filter((value) => value.includes('.'))
    .forEach((value) => addRuleMatchKey(keys, value))

  optionParts
    .filter((value) => !value.includes('.'))
    .forEach((value) => addRuleMatchKey(keys, value))

  return keys
}

const addRuleReadableMapKeys = (keys = [], mapKey = '') => {
  let keyList = String(mapKey || '')
    .split('.')
    .filter((key) => key)

  addRuleFieldCandidateKey(keys, keyList.join('.'))

  keyList.forEach((key, index) => {
    addRuleFieldCandidateKey(keys, keyList.slice(index).join('.'))
  })

  return keys
}

const addRuleFieldMapKeys = (
  keys = [],
  jsonForm = {},
  fieldKey = '',
  ui = {},
) => {
  let mapKey = objectPath.get(jsonForm, 'schema.mapKey') || {}
  let mapName = objectPath.get(jsonForm, 'schema.mapName') || {}
  let fieldName = getFieldKey(fieldKey)
  let matchKeys = uniqueRuleKeys([
    ui.key,
    objectPath.get(ui, 'input.key'),
    fieldKey,
    fieldName,
    ui.name,
    ui.value,
    objectPath.get(ui, 'input.name'),
    objectPath.get(ui, 'input.value'),
  ])

  const addSchemaMapKeys = (schemaMap = {}, isDataMap = false) => {
    Object.keys(schemaMap).forEach((mapFieldKey) => {
      let mapValue = schemaMap[mapFieldKey] || ''
      let isMatched =
        matchRuleKeys(mapFieldKey, matchKeys) ||
        matchKeys.some((key) => matchRulePathKey(mapValue, key))

      if (!isMatched) return

      addRuleFieldCandidateKey(keys, mapFieldKey)
      if (isDataMap) {
        addRuleReadableMapKeys(keys, mapValue)
      }
    })
  }

  addSchemaMapKeys(mapKey, true)
  addSchemaMapKeys(mapName)

  return keys
}

const getRuleFieldKeys = (jsonForm = {}, fieldKey) => {
  let fieldName = getFieldKey(fieldKey)
  let ui = findRuleUi(
    objectPath.get(jsonForm, 'uischema.elements') || [],
    fieldName,
  )

  if (!ui) {
    let keys = addRuleFieldMapKeys([], jsonForm, fieldKey)
    addRuleFieldCandidateKey(keys, fieldName)

    return uniqueRuleKeys(keys)
  }

  return uniqueRuleKeys(
    addRuleFieldMapKeys(
      [
        ui.key,
        objectPath.get(ui, 'input.key'),
        fieldName,
        ui.name,
        ui.value,
        objectPath.get(ui, 'input.name'),
        objectPath.get(ui, 'input.value'),
      ].reduce((keys, key) => addRuleFieldCandidateKey(keys, key), []),
      jsonForm,
      fieldKey,
      ui,
    ),
  )
}

const getRuleDefaultValue = (jsonForm = {}, fieldKey) => {
  let ui = findRuleUi(
    objectPath.get(jsonForm, 'uischema.elements') || [],
    fieldKey,
  )

  if (!ui) return undefined
  if (objectPath.has(ui, 'defaultValue'))
    return objectPath.get(ui, 'defaultValue')
  if (objectPath.has(ui, 'input.defaultValue')) {
    return objectPath.get(ui, 'input.defaultValue')
  }
  if (objectPath.has(ui, 'defaultChecked')) {
    return objectPath.get(ui, 'defaultChecked')
  }
  return undefined
}

const getRuleSourceFieldValue = (sourceData, key, source) => {
  let value = objectPath.get(sourceData, key)

  if (value === undefined && source === 'formData' && key) {
    value = objectPath.get(sourceData, `@${key}`)
  }

  return value
}

const parseRuleConfigData = (value) => {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch (error) {
      return {}
    }
  }

  return value || {}
}

const getRuleConfigCode = (config = {}) => {
  return config.configCode || config.code || ''
}

const getRuleConfigList = (designContext = {}, data = {}) => {
  let { jsonForm = {} } = designContext
  let config =
    data.config || jsonForm.accountConfig || designContext.config || []

  return Array.isArray(config) ? config : []
}

const getRuleConfigFieldData = (field = '', configCode = '', dataKey = '') => {
  let fieldText = String(field || '')

  if (configCode) {
    return {
      configCode,
      dataKey:
        dataKey ||
        (fieldText.indexOf(`${configCode}.`) === 0
          ? fieldText.slice(configCode.length + 1)
          : fieldText === configCode
          ? ''
          : fieldText),
    }
  }

  return {
    configCode: '',
    dataKey,
  }
}

const getRuleConfigValue = (
  designContext = {},
  { field = '', configCode = '', dataKey = '' } = {},
  data = {},
) => {
  let configList = getRuleConfigList(designContext, data)
  let configFieldData = getRuleConfigFieldData(field, configCode, dataKey)
  let foundConfig = configFieldData.configCode
    ? configList.find(
        (config) => getRuleConfigCode(config) === configFieldData.configCode,
      )
    : configList.find((config) => {
        let currentConfigCode = getRuleConfigCode(config)

        return (
          field === currentConfigCode ||
          String(field || '').indexOf(`${currentConfigCode}.`) === 0
        )
      })

  if (!foundConfig) return undefined

  let targetConfigCode = getRuleConfigCode(foundConfig)
  let targetDataKey =
    configFieldData.dataKey ||
    (String(field || '').indexOf(`${targetConfigCode}.`) === 0
      ? String(field).slice(targetConfigCode.length + 1)
      : '')
  let configData = parseRuleConfigData(foundConfig.jsonData ?? foundConfig.data)

  if (!targetDataKey) return configData

  return objectPath.get(configData, targetDataKey)
}

export const getRuleValue = (
  designContext = {},
  { source = 'formData', field = '', configCode = '', dataKey = '' } = {},
  data = {},
) => {
  let { jsonForm = {} } = designContext
  let fieldKey = getFieldKey(field)
  if (source === 'localStorage') {
    return typeof localStorage !== 'undefined'
      ? localStorage.getItem(fieldKey)
      : undefined
  }
  if (source === 'config') {
    return getRuleConfigValue(
      designContext,
      { field, configCode, dataKey },
      data,
    )
  }

  let sourceData = getSourceData(source, designContext, data)
  let value

  getRuleFieldKeys(jsonForm, fieldKey).find((key) => {
    value = getRuleSourceFieldValue(sourceData, key, source)
    return value !== undefined
  })

  if (value === undefined && fieldKey !== field) {
    value = getRuleSourceFieldValue(sourceData, field, source)
  }
  if (value === undefined && source === 'formData' && data.isDefaultLoad) {
    value = getRuleDefaultValue(jsonForm, fieldKey)
  }
  return value
}

const getRuleCompareValue = (designContext = {}, condition = {}, data = {}) => {
  let compareWith = condition.compareWith || ''
  let compareTo = condition.compareTo || {}

  if (compareWith === 'today' || compareTo.tag === 'today') {
    return new Date()
  }

  if (compareWith === 'system' || compareTo.field || compareTo.optionValue) {
    return getRuleValue(
      designContext,
      {
        source: compareTo.source || 'formData',
        field:
          compareTo.optionValue ||
          compareTo.field ||
          compareTo.valueKey ||
          compareTo.key ||
          compareTo.name ||
          '',
        configCode: compareTo.configCode || '',
        dataKey: compareTo.dataKey || '',
      },
      data,
    )
  }

  return condition.value
}

const getRuleConditionValueKeys = (condition = {}) => {
  return uniqueRuleKeys(
    [
      condition.fullKey,
      condition.fieldPath,
      condition.optionKey,
      condition.optionValue,
      condition.valueKey,
      condition.fieldKey,
      condition.key,
      condition.name,
      condition.field,
    ].reduce((keys, key) => addRuleFieldOptionKeyParts(keys, key), []),
  )
}

const addRuleFormComponentKey = (
  keys = [],
  formComponentName = '',
  key = '',
) => {
  if (!formComponentName || !key) return keys

  let fieldKey = getFieldKey(key)
  let lastFieldKey = String(fieldKey || '')
    .split('.')
    .filter((item) => item)
    .pop()

  addRuleMatchKey(keys, `${formComponentName}.${fieldKey}`)
  addRuleMatchKey(keys, `${formComponentName}.${lastFieldKey}`)

  return keys
}

const addRuleFormComponentName = (names = [], name = '') => {
  if (!name) return names
  if (names.includes(name)) return names

  names.push(name)
  return names
}

const getRuleDataFormComponentNames = (data = {}) => {
  let names = []

  addRuleFormComponentName(names, data.formComponentName)
  ;(Array.isArray(data.ruleUiKeys) ? data.ruleUiKeys : [])
    .map((key) => String(key || '').split('.').filter((item) => item))
    .filter((keyList) => keyList.length > 1)
    .forEach((keyList) => addRuleFormComponentName(names, keyList[0]))

  return names
}

const getRuleConditionFormComponentKeys = (
  condition = {},
  data = {},
) => {
  let formComponentNames = getRuleDataFormComponentNames(data)
  if (formComponentNames.length === 0) return []

  return getRuleConditionValueKeys(condition).reduce((keys, key) => {
    formComponentNames.forEach((formComponentName) =>
      addRuleFormComponentKey(keys, formComponentName, key),
    )

    return keys
  }, [])
}

const matchRuleChangedKey = (ruleKey, uiKey) => {
  let ruleMatchKey = normalizeRuleMatchKey(ruleKey)
  let uiMatchKey = normalizeRuleMatchKey(uiKey)

  if (!ruleMatchKey || !uiMatchKey) return false
  if (ruleKey === uiKey) return true
  if (getFieldKey(ruleKey) === uiKey) return true
  if (ruleMatchKey === uiMatchKey) return true
  if (isCrossScopeShortMatch(ruleKey, uiKey)) return false
  if (uiMatchKey.endsWith(`.${ruleMatchKey}`)) return true
  if (ruleMatchKey.endsWith(`.${uiMatchKey}`)) return true

  return false
}

const getRuleChangedValueData = (fields = [], data = {}) => {
  if (!objectPath.has(data, 'valueChange')) {
    return {
      hasValue: false,
    }
  }

  let ruleUiKeys = Array.isArray(data.valueChangeUiKeys)
    ? data.valueChangeUiKeys
    : Array.isArray(data.ruleUiKeys)
    ? data.ruleUiKeys
    : []
  let field = fields.find((field) =>
    ruleUiKeys.some((uiKey) => matchRuleChangedKey(field, uiKey)),
  )

  if (!field) {
    return {
      hasValue: false,
    }
  }

  return {
    hasValue: true,
    field,
    value: data.valueChange,
  }
}

const getRuleStatusValueData = (condition = {}, fields = [], data = {}) => {
  switch (condition.compare) {
    case 'isOnClick':
      return {
        hasValue: true,
        field: fields[0] || '',
        value: data.isOnClick ? true : false,
      }
    case 'isAfterSuccess':
      return {
        hasValue: true,
        field: fields[0] || '',
        value: data.isAfterSuccess ? true : false,
      }
    default:
      return {
        hasValue: false,
      }
  }
}

const getRuleConditionValueData = (
  designContext,
  condition = {},
  data = {},
) => {
  let source = condition.source || 'formData'
  let fields = getRuleConditionValueKeys(condition)
  if (source === 'formData') {
    fields = uniqueRuleKeys([
      ...fields,
      ...getRuleConditionFormComponentKeys(condition, data),
    ])
  }
  let matchedField = fields[0] || ''
  let value
  let statusValueData = getRuleStatusValueData(condition, fields, data)
  if (statusValueData.hasValue) {
    return {
      value: statusValueData.value,
      field: statusValueData.field,
      fields,
    }
  }

  let changedValueData =
    source === 'formData' ? getRuleChangedValueData(fields, data) : {}

  if (changedValueData.hasValue) {
    return {
      value: changedValueData.value,
      field: changedValueData.field,
      fields,
    }
  }

  if (source === 'config') {
    let field =
      condition.optionValue ||
      condition.valueKey ||
      condition.fullKey ||
      condition.field ||
      ''

    return {
      value: getRuleValue(
        designContext,
        {
          source,
          field,
          configCode: condition.configCode || '',
          dataKey: condition.dataKey || '',
        },
        data,
      ),
      field: condition.dataKey || condition.field || matchedField,
      fields,
    }
  }

  fields.find((field) => {
    value = getRuleValue(
      designContext,
      {
        source,
        field,
      },
      data,
    )

    if (value === undefined) return false

    matchedField = field
    return true
  })

  return {
    value,
    field: matchedField,
    fields,
  }
}

export const checkRuleCondition = (
  designContext,
  condition = {},
  data = {},
) => {
  let { value } = getRuleConditionValueData(designContext, condition, data)
  let conditionValue = getRuleCompareValue(designContext, condition, data)

  return compareRuleValue(condition.compare, value, conditionValue, condition)
}

const isRuleConditionGroup = (condition = {}) => condition.type === 'group'

const getRuleConditionOperator = (conditionOperator = 'AND') => {
  return String(conditionOperator || '').toUpperCase() === 'OR' ? 'OR' : 'AND'
}

const getRuleConditionList = (conditions = []) => {
  return Array.isArray(conditions) ? conditions : []
}

const checkRuleConditionGroup = (
  designContext,
  condition = {},
  data = {},
) => {
  return checkRuleConditions(
    designContext,
    getRuleConditionList(condition.conditions),
    getRuleConditionOperator(condition.conditionOperator),
    data,
  )
}

const checkRuleConditionItem = (
  designContext,
  condition = {},
  data = {},
) => {
  return isRuleConditionGroup(condition)
    ? checkRuleConditionGroup(designContext, condition, data)
    : checkRuleCondition(designContext, condition, data)
}

export const checkRuleConditions = (
  designContext,
  conditions = [],
  conditionOperator = 'AND',
  data = {},
) => {
  let conditionList = getRuleConditionList(conditions)
  let operator = getRuleConditionOperator(conditionOperator)

  if (conditionList.length === 0) return operator === 'OR' ? false : true

  if (operator === 'OR') {
    return conditionList.some((condition) =>
      checkRuleConditionItem(designContext, condition, data),
    )
  }

  return conditionList.every((condition) =>
    checkRuleConditionItem(designContext, condition, data),
  )
}

const getRuleElseIfBranches = (rule = {}) => {
  if (Array.isArray(rule.elseIf)) return rule.elseIf
  if (Array.isArray(rule.elseIfs)) return rule.elseIfs
  return []
}

const getRuleBranches = (rule = {}) => [
  {
    id: `${rule.id || 'rule'}-if`,
    branchType: 'if',
    conditionOperator: getRuleConditionOperator(rule.conditionOperator),
    conditions: getRuleConditionList(rule.conditions),
    actions: rule.actions || [],
  },
  ...getRuleElseIfBranches(rule).map((branch, index) => ({
    ...branch,
    id: branch.id || `${rule.id || 'rule'}-else-if-${index}`,
    branchType: 'elseIf',
    conditionOperator: getRuleConditionOperator(branch.conditionOperator),
    conditions: getRuleConditionList(branch.conditions),
    actions: branch.actions || [],
  })),
]

const addRuleOptionKeyParts = (keys = [], key = '') => {
  addRuleMatchKey(keys, key)

  String(key || '')
    .split('|')
    .map((value) => value.trim())
    .filter((value) => value)
    .forEach((value) => addRuleMatchKey(keys, value))

  return keys
}

const getRuleConditionMatchKeys = (condition = {}) => {
  return uniqueRuleKeys(
    [
      condition.fieldKey,
      condition.key,
      condition.valueKey,
      condition.optionValue,
      condition.field,
      condition.fieldPath,
      condition.fullKey,
      condition.optionKey,
      condition.name,
    ].reduce((keys, key) => addRuleOptionKeyParts(keys, key), []),
  )
}

const getRuleConditionTriggerFieldsFromCondition = (condition = {}) => {
  if (isRuleConditionGroup(condition)) return []
  if (condition.source && condition.source !== 'formData') return []

  return getRuleConditionValueKeys(condition).map((key) => ({
    key,
    source: 'formData',
    events: [
      {
        event: 'onChange',
        debounce: 500,
      },
    ],
  }))
}

const getRuleConditionTriggerFieldsFromConditions = (conditions = []) => {
  let triggerFields = []

  getRuleConditionList(conditions).forEach((condition) => {
    if (isRuleConditionGroup(condition)) {
      triggerFields = [
        ...triggerFields,
        ...getRuleConditionTriggerFieldsFromConditions(
          condition.conditions,
        ),
      ]
      return
    }

    getRuleConditionTriggerFieldsFromCondition(condition).forEach(
      (triggerField) => {
        if (triggerField) triggerFields.push(triggerField)
      },
    )
  })

  return triggerFields
}

const getRuleConditionTriggerFields = (rule = {}) => {
  let triggerFields = []

  getRuleBranches(rule).forEach((branch) => {
    getRuleConditionTriggerFieldsFromConditions(branch.conditions).forEach(
      (triggerField) => {
        if (triggerFields.find(({ key }) => key === triggerField.key)) return

        triggerFields.push(triggerField)
      },
    )
  })

  return triggerFields
}

const getRuleActionTriggerFieldKey = (action = {}) => {
  switch (action.type) {
    case 'ageToDate':
      return getRuleActionTarget(action)
    default:
      return ''
  }
}

const getRuleActionTriggerField = (action = {}) => {
  let key = getRuleActionTriggerFieldKey(action)
  if (!key) return null

  return {
    key,
    source: 'formData',
    events: [
      {
        event: 'onChange',
        debounce: 500,
      },
    ],
  }
}

const getRuleActionTriggerFields = (rule = {}) => {
  let triggerFields = []

  getRuleBranches(rule).forEach((branch) => {
    let branchActions = branch.actions || []
    branchActions.forEach((action) => {
      let triggerField = getRuleActionTriggerField(action)
      if (!triggerField) return
      if (triggerFields.find(({ key }) => key === triggerField.key)) return

      triggerFields.push(triggerField)
    })
  })

  return triggerFields
}

const getRuleSyncDataActions = (rule = {}) => {
  let actions = []

  getRuleBranches(rule).forEach((branch) => {
    let branchActions = branch.actions || []
    branchActions.forEach((action) => {
      if (action.type === 'syncData') {
        actions.push(action)
      }
    })
  })

  return actions
}

const checkRuleBranch = (
  designContext,
  branch = {},
  data = {},
) => {
  let conditions = getRuleConditionList(branch.conditions)

  if (conditions.length === 0) return true

  let conditionPass = checkRuleConditions(
    designContext,
    conditions,
    getRuleConditionOperator(branch.conditionOperator),
    data,
  )

  return conditionPass
}

const getRuleActionTargetMatchKeys = (
  designContext = {},
  action = {},
) => {
  let { jsonForm = {} } = designContext
  let target = getRuleActionTarget(action)
  let keys = []

  addRuleOptionKeyParts(keys, target)
  getRuleSearchKeys(jsonForm, target).forEach((key) =>
    addRuleOptionKeyParts(keys, key),
  )

  return uniqueRuleKeys(keys)
}

const isRuleActionTargetCondition = (
  designContext = {},
  action = {},
  condition = {},
) => {
  if (!isRuleFetchOptionAction(action)) return false

  let targetKeys = getRuleActionTargetMatchKeys(designContext, action)
  let conditionKeys = getRuleConditionMatchKeys(condition)
  if (targetKeys.length === 0 || conditionKeys.length === 0) return false

  return conditionKeys.some((conditionKey) =>
    matchRuleKeys(conditionKey, targetKeys),
  )
}

const getRuleActionConditionList = (
  designContext = {},
  action = {},
  conditions = [],
) => {
  return getRuleConditionList(conditions)
    .map((condition) => {
      if (isRuleConditionGroup(condition)) {
        let groupConditions = getRuleActionConditionList(
          designContext,
          action,
          condition.conditions,
        )

        if (groupConditions.length === 0) return null

        return {
          ...condition,
          conditions: groupConditions,
        }
      }

      return isRuleActionTargetCondition(designContext, action, condition)
        ? null
        : condition
    })
    .filter((condition) => condition)
}

const checkRuleActionConditions = (
  designContext,
  branch = {},
  action = {},
  data = {},
) => {
  let conditions = getRuleActionConditionList(
    designContext,
    action,
    branch.conditions,
  )

  if (conditions.length === 0) return true

  return checkRuleConditions(
    designContext,
    conditions,
    getRuleConditionOperator(branch.conditionOperator),
    data,
  )
}

const runRuleFetchOptionActions = (
  designContext = {},
  rule = {},
  data = {},
) => {
  let actionCount = 0

  getRuleBranches(rule).forEach((branch) => {
    let actions = branch.actions || []

    actions.forEach((action) => {
      if (!isRuleFetchOptionAction(action)) return
      let isPass = checkRuleActionConditions(designContext, branch, action, data)

      if (!isPass) return

      let result = runRuleActionSafe(
        designContext,
        action,
        data,
        rule,
      )
      actionCount += 1

      handleRuleActionPromise(result)
    })
  })

  return actionCount
}

export const getRuleMatchedBranch = (designContext, rule = {}, data = {}) => {
  if (!rule.enabled) return null

  return (
    getRuleBranches(rule).find((branch) =>
      checkRuleBranch(designContext, branch, data),
    ) || null
  )
}

export const checkRule = (designContext, rule = {}, data = {}) => {
  return Boolean(getRuleMatchedBranch(designContext, rule, data))
}

const getUpdatedRuleFormData = (formData = {}, uiKey, status = {}) => {
  if (!objectPath.has(status, 'valueChange')) return formData

  let uiKeys = Array.isArray(uiKey) ? uiKey : [uiKey]

  uiKeys
    .filter((key) => key)
    .forEach((key) => {
      objectPath.set(formData, key, status.valueChange)
    })

  return formData
}

const isRuleObjectPathWritable = (data = {}, key) => {
  let keyList = Array.isArray(key)
    ? key
    : String(key || '')
        .split('.')
        .filter((pathKey) => pathKey)
  let current = data

  for (let index = 0; index < keyList.length - 1; index += 1) {
    let pathKey = keyList[index]
    let currentValue = current[pathKey]

    if (currentValue === undefined || currentValue === null) return true
    if (typeof currentValue !== 'object') return false

    current = currentValue
  }

  return true
}

const setRuleObjectPathValue = (data = {}, key, value) => {
  if (!isRuleObjectPathWritable(data, key)) return false

  try {
    objectPath.set(data, key, value)
    return true
  } catch (error) {
    return false
  }
}

const getRuleActionTarget = (action = {}) => {
  return (
    action.target ||
    action.targetKey ||
    action.key ||
    action.valueKey ||
    action.targetName ||
    action.targetOptionValue ||
    action.formDataKey ||
    ''
  )
}

const getRuleActionValue = (designContext = {}, action = {}, data = {}) => {
  let mode = action.mode && action.mode !== '-' ? action.mode : 'value'

  if (mode === 'field') {
    let valueFrom = action.valueFrom || {}
    return getRuleValue(
      designContext,
      {
        source: valueFrom.source || 'formData',
        field:
          valueFrom.optionValue ||
          valueFrom.field ||
          valueFrom.valueKey ||
          valueFrom.key ||
          valueFrom.name ||
          '',
      },
      data,
    )
  }

  if (mode === 'today') return new Date()
  if (mode === 'userId') return objectPath.get(designContext, 'user.userId')
  if (mode === 'employeeCode') {
    return objectPath.get(designContext, 'user.employeeCode')
  }

  return action.value
}

const getRuleSyncFieldKey = (field = {}) => {
  return field.key || field.fieldKey || field.optionValue || ''
}

const getRuleSyncFieldValueKey = (field = {}, valueKeyFallback = '') => {
  return (
    field.valueKey ||
    valueKeyFallback ||
    field.field ||
    field.name ||
    field.optionValue ||
    field.key ||
    field.fieldPath ||
    ''
  )
}

const normalizeRuleSyncField = (field = {}, valueKeyFallback = '') => {
  if (!field) return null

  return {
    ...field,
    key: getRuleSyncFieldKey(field),
    valueKey: getRuleSyncFieldValueKey(field, valueKeyFallback),
  }
}

const normalizeRuleSyncAction = (action = {}) => {
  let syncFrom = normalizeRuleSyncField(action.syncFrom)
  let syncTo = normalizeRuleSyncField(
    action.syncTo,
    action.value || action.syncName,
  )

  return {
    ...action,
    syncFrom,
    syncTo,
  }
}

const matchDynamicSyncField = (field = {}, status = {}) => {
  let syncField = normalizeRuleSyncField(field)
  let syncKeys = [syncField?.valueKey, syncField?.key]
  let dynamicKeys = [status.dynamicName, status.dynamicKey, status.name]

  return syncKeys
    .filter((key) => key)
    .some((syncKey) =>
      dynamicKeys
        .filter((key) => key)
        .some((dynamicKey) => matchRuleKey(syncKey, dynamicKey)),
    )
}

const matchArraySyncField = (field = {}, status = {}) => {
  let syncField = normalizeRuleSyncField(field)
  let syncKeys = [syncField?.valueKey, syncField?.key]
  let arrayKeys = [
    status.arrayName,
    status.arrayKey,
    status.name,
    ...(Array.isArray(status.ruleUiKeys) ? status.ruleUiKeys : []),
  ]

  return syncKeys
    .filter((key) => key)
    .some((syncKey) =>
      arrayKeys
        .filter((key) => key)
        .some((arrayKey) => matchRuleKey(syncKey, arrayKey)),
    )
}

const matchDynamicSyncEvent = (rule = {}, eventKey) => {
  if (!eventKey) return true

  let trigger = rule.trigger || {}
  let triggerFields = getTriggerFields(trigger)

  return (
    triggerFields.find((triggerField) =>
      matchRuleEvent(triggerField, trigger, eventKey),
    ) || matchRuleEvent({}, trigger, eventKey)
  )
}

const matchDynamicSyncDataRule = (rule = {}, status = {}) => {
  if (!status.isDynamic) return false
  if (!rule.enabled) return false
  if (!matchDynamicSyncEvent(rule, status.eventKey)) return false

  return getRuleSyncDataActions(rule).some((action) =>
    matchDynamicSyncField(action.syncFrom, status),
  )
}

const matchArraySyncDataRule = (rule = {}, status = {}) => {
  if (!status.isArray) return false
  if (!rule.enabled) return false
  if (!matchDynamicSyncEvent(rule, status.eventKey)) return false

  return getRuleSyncDataActions(rule).some((action) =>
    matchArraySyncField(action.syncFrom, status),
  )
}

const mergeRuleList = (rules = [], addRules = []) => {
  let result = [...rules]

  addRules.forEach((rule) => {
    let foundRule = result.find((item) =>
      rule.id ? item.id === rule.id : item === rule,
    )
    if (!foundRule) {
      result.push(rule)
    }
  })

  return result
}

const getDynamicSyncDataRules = (jsonForm = {}, status = {}) => {
  if (!status.isDynamic) return []

  return getRules(jsonForm).filter((rule) =>
    matchDynamicSyncDataRule(rule, status),
  )
}

const getArraySyncDataRules = (jsonForm = {}, status = {}) => {
  if (!status.isArray) return []

  return getRules(jsonForm).filter((rule) =>
    matchArraySyncDataRule(rule, status),
  )
}

const getMatchedRules = (jsonForm = {}, ruleUiKey = null, status = {}) => {
  let rules = searchRules(jsonForm, ruleUiKey, status)
  let dynamicSyncRules = getDynamicSyncDataRules(jsonForm, status)
  let arraySyncRules = getArraySyncDataRules(jsonForm, status)

  return mergeRuleList(mergeRuleList(rules, dynamicSyncRules), arraySyncRules)
}

const getRuleSyncStatus = (designContext = {}, action = {}, status = {}) => {
  let { jsonForm = {} } = designContext
  let syncToKey = action.syncTo?.key
  let syncToUi = syncToKey
    ? getCurrentUi(syncToKey, jsonForm.schema, jsonForm.uischema)
    : null

  if (syncToUi?.key === syncToKey && syncToUi.tagname === 'dynamic') {
    return {
      ...status,
      isArray: true,
      arrayKey: status.arrayKey || action.syncFrom?.key || syncToKey,
    }
  }

  return status
}

const runRuleSyncData = (
  designContext = {},
  action = {},
  data = {},
  elementAction = {},
) => {
  let { jsonForm = {} } = designContext
  let syncAction = normalizeRuleSyncAction(action)
  let syncStatus = getRuleSyncStatus(designContext, syncAction, data)
  let formData = data.formData || jsonForm.formData || {}

  return onSyncData(
    syncAction,
    formData,
    elementAction,
    designContext,
    syncStatus,
  )
}

export const setRuleFormData = (designContext = {}, target, value) => {
  if (!target) return

  let {
    jsonForm = {},
    setFieldFormData,
    setFieldAllData,
    setFormData,
    isUiChanged = {},
    setIsUiChanged,
  } = designContext
  let formData = jsonForm.formData || {}
  let targetKeys = getRuleFieldKeys(jsonForm, target)

  targetKeys.forEach((key) => {
    let isUpdated = setRuleObjectPathValue(formData, key, value)
    if (!isUpdated) return
    setRuleObjectPathValue(isUiChanged, key, { isRender: true })
    if (setFieldFormData) setFieldFormData(key, value)
    if (setFieldAllData) setFieldAllData(key, value)
  })
  if (setFormData) setFormData(formData)
  if (setIsUiChanged) setIsUiChanged(isUiChanged)
}

export const setRuleElementAction = (
  designContext = {},
  action = {},
  value,
) => {
  let { elementAction = {}, setElementAction } = designContext
  let target = getRuleActionTarget(action)
  if (!target) return

  switch (action.type) {
    case 'isWarning':
      objectPath.set(elementAction, ['isWarning', target], value || true)
      break
    default:
      return
  }

  if (setElementAction) setElementAction(elementAction)
}

export const runRuleAction = (
  designContext = {},
  action = {},
  data = {},
  rule = {},
) => {
  let { elementAction = {} } = designContext
  let target = getRuleActionTarget(action)

  switch (action.type) {
    case 'isCalculate':
      return onRuleCalculate(designContext, action, data, rule, {
        getRuleValue,
        setRuleFormData,
        target,
      })
    case 'dateToAge':
      return onRuleCalculateDate(designContext, action, data, {
        getRuleValue,
        setRuleFormData,
        target,
      })
    case 'ageToDate':
      return onRuleAgeToDate(designContext, action, data, {
        getRuleValue,
        setRuleFormData,
      })
    case 'dateToText':
      return onRuleDateToText(designContext, action, data, {
        getRuleValue,
        setRuleFormData,
      })
    case 'isSet':
      return onRuleSet(designContext, action, data, {
        getRuleActionValue,
        getRuleValue,
        setRuleFormData,
        target,
      })
    case 'updateValue':
      return onRuleUpdateValue(designContext, action, data, target)
    case 'syncData':
      return runRuleSyncData(designContext, action, data, elementAction)
    case 'updateOption':
      return onRuleFetchOption(designContext, action, data, target)
    case 'fetchTable':
      return onRuleFetchTable(designContext, action, target)
    case 'fetchComponent':
      return onRuleFetchComponent(designContext, action, target)
    case 'updateCheckboxList':
      return onRuleUpdateCheckboxList(designContext, action, data, target)
    case 'callAPI':
      return onRuleCallApi(designContext, action, data, rule)
    case 'callApi':
      return onRuleCallApi(designContext, action, data, rule)
    case 'callTableAPI':
      return onRuleCallTableApi(designContext, action, data, target)
    case 'selectRowTable':
      return onRuleSelectRowTable(designContext, action, data, target)
    case 'unselectRowTable':
      return onRuleUnselectRowTable(designContext, action, data, target)
    case 'activeButton':
      return onRuleActiveButton(designContext, action, data, target)
    case 'clickButton':
      return onRuleClickButton(designContext, action, data, target)
    case 'activeTab':
      return onRuleActiveTab(designContext, action, data, target)
    case 'clearActive':
      return onRuleClearActive(designContext, action, data, target)
    case 'clearActiveButton':
      return onRuleClearActiveButton(designContext)
    case 'clearActiveTab':
      return onRuleClearActiveTab(designContext, action, target)
    case 'isLoading':
      return onRuleLoading(designContext, action, target)
    case 'isDoneLoading':
      return onRuleDoneLoading(designContext, action, target)
    case 'openModal':
      return onRuleOpenModal(designContext, action, data, target)
    case 'closeModal':
      return onRuleCloseModal(designContext)
    case 'successModal':
      return onRuleSweetAlert(designContext, action, data)
    case 'isReset':
      return onRuleReset(designContext, action, data, {
        setRuleFormData,
        target,
      })
    case 'isShow':
      return onRuleShow(designContext, action, data, target)
    case 'isHide':
      return onRuleHide(designContext, action, data, target)
    case 'isShowHideList':
      return onRuleShowHideList(designContext, action, data, target)
    case 'isEnable':
      return onRuleEnable(designContext, action, data, target)
    case 'isDisable':
      return onRuleDisable(designContext, action, data, target)
    case 'isColorLabel':
      return onRuleColorLabel(designContext, action, target)
    case 'isRequire':
      return onRuleRequire(designContext, action, data, {
        getRuleActionValue,
        target,
      })
    case 'isWarning':
      return onRuleWarning(designContext, action, data, {
        getRuleActionValue,
        target,
      })
    case 'goToUrl':
      return onRuleGoToUrl(designContext, action, data)
    case 'isCopy':
      return onRuleCopy(designContext, action, data)
    case 'isReload':
      return onRuleReload()
    case 'isShowLoadUI':
      return onRuleShowLoadUI(designContext, action, target)
    default:
      return undefined
  }
}

const isRuleActionPromise = (result) => {
  return result && typeof result.then === 'function'
}

const handleRuleActionPromise = (result) => {
  if (!isRuleActionPromise(result)) return

  result.then(
    () => undefined,
    () => undefined,
  )
}

const waitRuleActionPromises = async (promises = []) => {
  if (promises.length === 0) return

  await Promise.allSettled(promises)
}

const runRuleActionSafe = (
  designContext = {},
  action = {},
  data = {},
  rule = {},
) => {
  try {
    return runRuleAction(designContext, action, data, rule)
  } catch (error) {
    console.error('[RuleAction] run action error', error)
    return undefined
  }
}

const RULE_CASCADE_ACTION_TYPES = ['isSet', 'isReset']

const getRuleActionCascadeKeys = (
  jsonForm = {},
  action = {},
  target = '',
) => {
  let keys = []
  let actionKeys = [
    target,
    action.targetKey,
    action.key,
    action.valueKey,
    action.targetName,
    action.formDataKey,
    action.targetOptionValue,
  ]

  actionKeys.forEach((actionKey) => {
    getRuleFieldKeys(jsonForm, actionKey).forEach((key) => {
      addRuleMatchKey(keys, key)
    })
  })

  return uniqueRuleKeys(keys)
}

const runRuleActionCascade = (
  designContext = {},
  action = {},
  data = {},
  rule = {},
) => {
  if (!RULE_CASCADE_ACTION_TYPES.includes(action.type)) return

  let { jsonForm = {} } = designContext
  let target = getRuleActionTarget(action)
  let ruleUiKeys = getRuleActionCascadeKeys(jsonForm, action, target)
  if (!ruleUiKeys.length) return

  let ruleActionStack = Array.isArray(data.ruleActionStack)
    ? data.ruleActionStack
    : []
  let exceptRuleIds = [...ruleActionStack, rule.id].filter((ruleId) => ruleId)
  let cascadeData = { ...data }
  delete cascadeData.valueChange
  delete cascadeData.valueChangeUiKeys

  onCheckRules(designContext, jsonForm.formData || data.formData, ruleUiKeys, {
    ...cascadeData,
    eventKey: 'onChange',
    isOnChange: true,
    isDebounce: false,
    exceptRuleIds,
    ruleActionStack: exceptRuleIds,
    ruleUiKeys,
  })
}

export const runRuleActions = (
  designContext = {},
  rule = {},
  data = {},
  branch = null,
) => {
  let targetBranch = branch || getRuleMatchedBranch(designContext, rule, data)
  let actions = targetBranch?.actions || []

  if (actions.length === 0) return Promise.resolve([])

  return actions.reduce(
    async (pendingPromise, action) => {
      let pendingActions = await pendingPromise

      if (isRuleCallApiWaitMode(action)) {
        await waitRuleActionPromises(pendingActions)
        pendingActions = []
      }

      let result = runRuleActionSafe(
        designContext,
        action,
        data,
        rule,
      )

      handleRuleActionPromise(result)
      runRuleActionCascade(designContext, action, data, rule)

      if (isRuleActionPromise(result)) {
        pendingActions.push(result)
      }

      if (isRuleCallApiWaitMode(action)) {
        await waitRuleActionPromises(pendingActions)
        pendingActions = []
      }

      return pendingActions
    },
    Promise.resolve([]),
  )
}

const runRuleActionQueue = async (actionQueue = []) => {
  for (let index = 0; index < actionQueue.length; index += 1) {
    await actionQueue[index]()
  }
}

const getRuleSourceFormData = (formData = {}) => {
  try {
    return JSON.parse(JSON.stringify(formData))
  } catch (error) {
    return { ...formData }
  }
}

export const onCheckRules = (
  designContext,
  data = null,
  uiKey = null,
  status = {},
) => {
  let { jsonForm } = designContext
  let formData = getUpdatedRuleFormData(data || jsonForm.formData, uiKey, status)
  let ruleUiKey = status.ruleUiKeys || uiKey
  let exceptRuleIds = Array.isArray(status.exceptRuleIds)
    ? status.exceptRuleIds
    : []
  let rules = getMatchedRules(jsonForm, ruleUiKey, status).filter(
    (rule) => !exceptRuleIds.includes(rule.id),
  )
  let ruleData = {
    ...status,
    formData,
    sourceFormData: getRuleSourceFormData(formData),
  }
  let actionQueue = []

  rules.forEach((rule) => {
    let matchedBranch = getRuleMatchedBranch(designContext, rule, ruleData)
    if (matchedBranch) {
      actionQueue.push(() =>
        runRuleActions(designContext, rule, ruleData, matchedBranch),
      )
    } else {
      actionQueue.push(() => {
        runRuleFetchOptionActions(designContext, rule, ruleData)

        return Promise.resolve([])
      })
    }
  })

  runRuleActionQueue(actionQueue).catch((error) => {
    console.error('[Rule] run action queue error', error)
  })

  return formData
}

export const onUpdateRules = (
  props = {},
  designContext,
  uiKey,
  data,
  status = {},
) => {
  let { jsonForm = {} } = designContext
  let ruleUiKey = status.ruleUiKeys || uiKey
  let ruleSearchKey = getRuleSearchKeys(jsonForm, ruleUiKey)
  let rules = getMatchedRules(jsonForm, ruleUiKey, status)
  if (rules.length === 0) return

  let {
    isDebounce = true,
    delayTime,
    debounceName = `on-input-update-rule-${uiKey}`,
  } = status
  let triggerEvent = rules
    .map((rule) => getMatchedTriggerEvent(rule, ruleSearchKey, status.eventKey))
    .find((event) => event)
  let ruleDelayTime = Number(delayTime ?? triggerEvent?.debounce ?? 500)

  if (isDebounce) {
    debounceCallback(
      onCheckRules,
      [designContext, data, uiKey, status],
      debounceName,
      ruleDelayTime,
    )
  } else {
    onCheckRules(designContext, data, uiKey, status)
  }
}
